import { Box } from '@mentimeter/ragnar-ui/box';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import type { Visualization } from '../types';
import { SvgWithFont } from '../utils/SvgWithFont';
import type {
  RenderChoiceLabel,
  RenderChoiceVisualization,
  RenderChoiceVisualizationItem,
} from '../utils/types/render-options-by-slide-type/types';
import { ScalesArea } from './ScalesArea';
import { ScalesAverageScore } from './ScalesAverageScore';
import { ScalesDimensionLabels } from './ScalesDimensionLabels';
import { ScalesDistribution } from './ScalesDistribution';
import { ScalesOptionLabels } from './ScalesOptionLabels';
import { ScalesOverallAverage } from './ScalesOverallAverage';
import { ScalesSkips } from './ScalesSkips';
import {
  MARGIN_BOTTOM,
  MARGIN_HORIZONTAL,
  MAX_ITEM_HEIGHT,
  MIN_PADDING,
} from './constants';
import { getOverallAverage } from './helpers';

export type ScalesScore = Record<string, number>;

export interface ScalesData {
  id: string;
  score: ScalesScore;
  skips: number;
}

export interface ScalesOptions {
  label: string;
  id: string;
}

interface ScaleValueClickProps {
  choiceId: ScalesOptions['id'];
  value: number;
}

interface ScalesProps
  extends Omit<Visualization<ScalesData, ScalesOptions>, 'valueAccessor'> {
  rangeMin: number;
  rangeMax: number;
  rangeMinLabel: string;
  rangeMaxLabel: string;
  valueAccessor?: (d: ScalesData) => ScalesScore;
  skipValueAccessor?: (d: ScalesData) => number;
  isShowAverageEnabled?: boolean;
  overallAverageColor: string;
  onScaleValueClick: ((props: ScaleValueClickProps) => void) | undefined;
  activeScale: {
    choiceId: ScalesOptions['id'] | null;
    value: number | null;
  };
  backgroundColor: string;
  renderChoiceVisualization?: RenderChoiceVisualization | undefined;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
}

// get max score of array of scores
const getMaxScore = (d: ScalesScore) => Math.max(...Object.values(d));

export const Scales = ({
  width,
  height,
  staticOptions,
  rangeMin,
  rangeMax,
  rangeMinLabel,
  rangeMaxLabel,
  data,
  colors,
  textColor,
  backgroundColor,
  overallAverageColor,
  isShowAverageEnabled = false,
  disableAnimations = false,
  keyAccessor = (d: ScalesData | ScalesOptions) => d.id.toString(),
  labelAccessor = (d: ScalesOptions) => d.label,
  valueAccessor = (d: ScalesData) => d.score,
  skipValueAccessor = (d: ScalesData) => d.skips,
  onScaleValueClick,
  activeScale,
  fontFamily,
  renderChoiceLabel,
  renderChoiceVisualization,
}: ScalesProps) => {
  const t = useTranslations('presenting');
  const [showDistribution, setShowDistribution] = useState<
    string | undefined
  >();

  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);
  const dimensionsLabelsContainerHeight = rootHeight * 0.2;

  const svgWidth = Math.max(rootWidth, 0);
  const svgHeight = Math.max(rootHeight - dimensionsLabelsContainerHeight, 0);
  const boundedSvgWidth = Math.max(rootWidth - MARGIN_HORIZONTAL * 2, 0);

  const maxBoundedSvgHeight = MAX_ITEM_HEIGHT * staticOptions.length;
  const boundedSvgHeight = Math.min(
    Math.max(svgHeight - MARGIN_BOTTOM * 2, 0),
    maxBoundedSvgHeight,
  );

  const discreteValues = staticOptions.map(keyAccessor);
  const fillColors = scaleOrdinal(colors).domain(discreteValues);

  const scalablePaddingValue = Math.max(
    0.2 - 0.025 * (staticOptions.length - 3),
    0,
  );
  const paddingInner = staticOptions.length > 1 ? scalablePaddingValue : 0; // dont apply padding if there is only one item
  const yScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedSvgHeight])
    .paddingInner(paddingInner);

  const itemHeight = Math.min(yScale.bandwidth(), MAX_ITEM_HEIGHT);
  const areaMarginTop = itemHeight * 0.15;
  const areaMarginBottom = itemHeight * 0.05;
  const boundsTopMargin = (svgHeight - boundedSvgHeight) / 2;

  const maxScore = Math.max(
    ...data.map((d) => getMaxScore(valueAccessor(d))),
    1, // we need a max value > 0 to be able to animate to 0
  );
  const itemYScale = scaleLinear()
    .domain([0, maxScore])
    .range([itemHeight - areaMarginBottom, areaMarginTop]);

  const xScale = scaleLinear()
    .domain([rangeMin, rangeMax])
    .range([0, boundedSvgWidth]);

  const overallAverage = getOverallAverage(data, rangeMin);
  const hasData = data.length > 0;

  const getRectanglePosition = useCallback(
    (id: string) => {
      const y = (yScale(id) ?? 0) + boundsTopMargin;
      const x = 0;
      const width = boundedSvgWidth;
      const step = yScale.step();
      const height = itemHeight;
      const gap = step - height;
      return { x, y, width, height, gap };
    },
    [yScale, boundsTopMargin, boundedSvgWidth, itemHeight],
  );

  const styleNeededForOverallAverageToBeVisible = { overflow: 'visible' };

  const visualization = (
    <SvgWithFont
      width={svgWidth}
      height={svgHeight}
      style={styleNeededForOverallAverageToBeVisible}
      fontFamily={fontFamily}
    >
      <g
        data-testid="horizontal-bounds-container"
        style={{
          transform: `translateX(${MARGIN_HORIZONTAL}px)`,
        }}
      >
        {isShowAverageEnabled && hasData && (
          <ScalesOverallAverage
            color={overallAverageColor}
            overallAverage={overallAverage}
            textColor={textColor}
            xScale={xScale}
            height={svgHeight}
            disableAnimations={disableAnimations}
          />
        )}
        <g
          data-testid="vertical-bounds-container"
          style={{
            transform: `translateY(${boundsTopMargin}px)`,
          }}
        >
          {staticOptions.map((option) => {
            const id = keyAccessor(option);
            const value = data.find((d) => keyAccessor(d) === id);
            const scoresValue = value ? valueAccessor(value) : undefined;
            const hasScores =
              scoresValue && Object.keys(scoresValue).length > 0;
            const scores = hasScores ? scoresValue : undefined;
            const skips = value ? skipValueAccessor(value) : 0;
            const fill = fillColors(id);

            const isShowingDistribution = showDistribution === id;
            return (
              <g
                key={id}
                data-testid="option-container"
                onMouseEnter={() => setShowDistribution(id)}
                onMouseLeave={() => setShowDistribution(undefined)}
                style={{
                  transform: `translate(0,${yScale(id)}px)`,
                }}
              >
                <rect
                  data-testid="scale-hover-area-expander"
                  x={0}
                  y={0}
                  width={boundedSvgWidth}
                  height={itemHeight}
                  opacity={0}
                />
                {scores && (
                  <ScalesArea
                    fill={fill}
                    scores={scores}
                    xScale={xScale}
                    yScale={itemYScale}
                    showDistribution={isShowingDistribution}
                    paddingBottom={Math.max(itemHeight * 0.1, MIN_PADDING)}
                    disableAnimations={disableAnimations}
                  />
                )}
                <ScalesAverageScore
                  scores={scores ?? {}}
                  fill={fill}
                  xScale={xScale}
                  height={itemHeight}
                  shouldShowAverageCircle={isShowingDistribution || !hasData}
                  totalWidth={boundedSvgWidth}
                  y={itemHeight}
                  placeholderBarColor={textColor}
                  disableAnimations={disableAnimations}
                />

                {scores && (
                  <>
                    <ScalesDistribution
                      isVisible={isShowingDistribution}
                      fill={fill}
                      outlineColor={backgroundColor}
                      scores={scores}
                      xScale={xScale}
                      height={itemHeight}
                      disableAnimations={disableAnimations}
                      onScaleValueClick={
                        onScaleValueClick &&
                        ((value: number) =>
                          onScaleValueClick({ choiceId: option.id, value }))
                      }
                      activeValue={
                        option.id === activeScale.choiceId
                          ? activeScale.value
                          : null
                      }
                    />
                    <ScalesSkips
                      x={boundedSvgWidth}
                      height={itemHeight}
                      isSkipsVisible={isShowingDistribution}
                      textColor={textColor}
                    >
                      {t.rich('scales.number_of_skips_message', {
                        skips,
                      })}
                    </ScalesSkips>
                  </>
                )}
              </g>
            );
          })}
        </g>
      </g>
    </SvgWithFont>
  );

  const visualizationChoices = useMemo(() => {
    if (typeof renderChoiceVisualization !== 'function') {
      return undefined;
    }

    const choices: Array<RenderChoiceVisualizationItem> = staticOptions.map(
      (option) => {
        const id = keyAccessor(option);
        const position = getRectanglePosition(id);
        return { id, position };
      },
    );

    return choices;
  }, [
    renderChoiceVisualization,
    staticOptions,
    keyAccessor,
    getRectanglePosition,
  ]);

  const visualizationOverride = renderChoiceVisualization?.({
    node: visualization,
    boundedWidth: boundedSvgWidth,
    boundsLeftMargin: MARGIN_HORIZONTAL,
    choices: visualizationChoices ?? [],
  });

  if (
    svgHeight <= 0 ||
    svgWidth <= 0 ||
    boundedSvgHeight <= 0 ||
    boundedSvgWidth <= 0
  ) {
    return null;
  }

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      justifyContent="center"
      alignItems="center"
      aria-hidden
    >
      <ScalesOptionLabels
        staticOptions={staticOptions}
        keyAccessor={keyAccessor}
        labelAccessor={labelAccessor}
        width={boundedSvgWidth}
        yScale={yScale}
        height={boundedSvgHeight}
        marginTop={boundsTopMargin}
        itemHeight={itemHeight}
        color={textColor}
        fontFamily={fontFamily}
        renderChoiceLabel={renderChoiceLabel}
      />
      {visualizationOverride ?? visualization}
      <ScalesDimensionLabels
        rangeMinLabel={rangeMinLabel}
        rangeMaxLabel={rangeMaxLabel}
        height={dimensionsLabelsContainerHeight}
      />
    </Box>
  );
};
